/**
 * MCP server entry point for `npx @sailfish-ai/autofix serve`
 *
 * Runs the Sailfish Autofix MCP server in STDIO mode.
 * All logs go to stderr — stdout is reserved for JSON-RPC messages.
 */

import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { AutofixBackendClient } from "../mcp-server/autofix-backend-client.js";
import { registerAutofixTools } from "../mcp-server/autofix-tools.js";
import { CREDENTIALS_FILE } from "../constants.js";
import { existsSync } from "fs";

export async function runServe(): Promise<void> {
  console.error("=".repeat(50));
  console.error("[Sailfish Autofix MCP] Starting...");
  console.error("=".repeat(50));

  // Resolve JWT file path — prefer env var, fall back to default credentials file
  let jwtFilePath = process.env.SAILFISH_JWT_FILE;
  if (!jwtFilePath) {
    if (existsSync(CREDENTIALS_FILE)) {
      jwtFilePath = CREDENTIALS_FILE;
      console.error(`[Sailfish Autofix MCP] Using credentials from ${CREDENTIALS_FILE}`);
    } else {
      console.error("[Sailfish Autofix MCP] ERROR: No credentials found.");
      console.error("[Sailfish Autofix MCP] Set SAILFISH_JWT_FILE or run `npx @sailfish-ai/autofix setup` first.");
      process.exit(1);
    }
  }

  // Create the backend client
  const client = new AutofixBackendClient(jwtFilePath);

  if (!client.isAvailable()) {
    console.error("[Sailfish Autofix MCP] WARNING: Credentials not found or invalid.");
    console.error("[Sailfish Autofix MCP] Tools will fail until you run `npx @sailfish-ai/autofix login`.");
  }

  // Create MCP server instance
  const server = new Server(
    {
      name: "sailfish-autofix",
      version: "1.0.0",
    },
    {
      capabilities: {
        tools: {},
      },
    }
  );

  // Register autofix tools
  registerAutofixTools(server, client);
  console.error("[Sailfish Autofix MCP] Tools registered");

  // Create stdio transport and connect
  const transport = new StdioServerTransport();
  await server.connect(transport);

  console.error("=".repeat(50));
  console.error("[Sailfish Autofix MCP] Ready and listening on stdio");
  console.error("=".repeat(50));

  // Graceful shutdown
  const shutdown = async () => {
    console.error("[Sailfish Autofix MCP] Shutdown signal received");
    process.exit(0);
  };

  process.on("SIGINT", shutdown);
  process.on("SIGTERM", shutdown);

  process.on("uncaughtException", (error) => {
    console.error("[Sailfish Autofix MCP] Uncaught exception:", error);
    process.exit(1);
  });

  process.on("unhandledRejection", (reason) => {
    console.error("[Sailfish Autofix MCP] Unhandled rejection:", reason);
    process.exit(1);
  });
}
